import {TinyEmitter} from 'tiny-emitter';
import {RoomStoreState} from "../store/room-defines";
import {RoomSharedState} from "../room-state-sync";
import {DrawObject} from "../draw-board";

export interface StreamPlayerMetas {
    name: string;
    version: string;
    decoder: string;
    latency: string;
    videoEngine?: string;
    audioEngine?: string;
}

export interface StreamPlayer extends TinyEmitter {

    /**
     * 获得流类型
     */
    getStreamType(): StreamType;

    /**
     * 获得视频的 DOM 元素
     */
    getVideoElement(): HTMLVideoElement | HTMLCanvasElement;

    /**
     * 初始化播放器
     */
    init(): Promise<StreamPlayerMetas>;

    /**
     * 播放流
     *
     * 如果指定了流地址，那么将替换已有流地址并播放；如果没有指定流地址，那么只是播放已暂停的流；
     *
     * @param src 流地址
     */
    play(src?: string): Promise<void>;

    /**
     * 暂停播放
     */
    pause(): void;

    /**
     * 播放跳转到指定时间点
     *
     * @param time 秒
     */
    seek(time: number): void;

    /**
     * 获得流地址
     */
    getSrc(): string | undefined;

    /**
     * 获取当前播放的时间
     */
    getCurrentTime(): number;

    /**
     * 销毁播放器
     */
    destroy(): void;

    /**
     * 重启播放器
     */
    restart(): Promise<void>;

    /**
     * 播放速度
     * @param speed
     */
    speed(speed: number): void;

    /**
     * 静音
     * @param mute
     */
    muted(mute: boolean): void;

    /**
     * 音量 0.0-1.0
     * @param volume
     */
    volume(volume: number): void;

    /**
     * 重绘视频播放画面
     *
     * 基于 canvas 实现的播放器，当页面大小发生变化时，需调用此方法，以修正视频画面，以免画面被拉伸或变模糊。
     */
    resize(videoRotate: number, pageRotate: number): void;

    supportKeepScreenOn(): boolean;
}

export interface StreamPlayerStats {
    [key: string]: string | number | undefined;
    streamId?: number;
    playerType?: string;
    baseBuffer: number;
    buffer: number;
    fps: number;
    bps: number;
    audioBps?: number;
    videoBps?: number;
    syncTime: number;
}

export interface Message {
    /**
     * 消息ID
     */
    mid: string;
    /**
     * 消息发送时间
     */
    ts: number;

    /**
     * 消息类型
     */
    cmd: string;

    /**
     * 是否时间轴对齐
     */
    align?: boolean;

    /**
     * 是否历史消息
     */
    history?: boolean;

    // eslint-disable-next-line
    [propName: string]: any;
}

export interface MessagerConnectParams {

    host: string;

    clientId: string;

    username: string;

    password: string;

    priRTopic: string;

    priWTopic: string;

    secRTopic: string;

    secWTopic: string;

    groupRTopic: string;

    groupWTopic: string;

    adminRTopic: string;
}

interface FeatureConfigs {
    [key: string]: unknown;
}

export interface RoomEnterParams {

    protocol: string;

    connect: MessagerConnectParams;

    token: string;

    ip: string;

    featureFlags: string;

    features: FeatureConfigs;
}

export interface ReplayParams {
    id: number;
    filesize: number;
    streamFilesize: number;
    duration: number;
    status: string;
    generateStartAt: number;
    generateEndAt: number;
    uri: string;
    showChat: boolean;
    domain: string;
    streamDomain: string;
    isDeleted: boolean;
}

export interface ReplayCanvasParams {
    contentId: number;
    page: number;
    uri: string;
    objects?: Array<DrawObject>;
}

export interface ReplayChatParams {
    startAt: number;
    endAt: number;
    uri: string;
    list?: Array<Message>;
}

export interface ReplayM3U8Params {
    duration: number;
    streamPosition: string;
    uri: string;
}

export interface ReplayStageParams extends RoomSharedState {
    createAt: number;
}

export interface ReplayDataParams {
    m3u8: ReplayM3U8Params[];
    chat: ReplayChatParams[];
    canvas: ReplayCanvasParams[];
    stage: string;
    page: string;
    syncTimeMode: string;
}

export interface ReplayPagePoint {
    page: number,
    title: string,
    ts: number,
}

export interface ReplayPageList {
    id: number,
    name: string,
    points: ReplayPagePoint[],
}

export interface ReplayPageData {
    version: number,
    data: ReplayPageList[],
}

/**
 * 直播间状态
 */
export enum RoomState {
    /**
     * 未开始
     */
    UNSTART = "unstart",

    /**
     * 直播中
     */
    LIVING = "living",

    /**
     * 暂停中
     */
    PAUSED = 'paused',

    /**
     * 已结束
     */
    FINISHED = "finished",

    /**
     * 开始中 (视图层独有）
     */
    STARTING = "starting",

    /**
     * 结束中 (视图层独有）
     */
    FINISHING = "finishing",
}

export enum RoomMode {
    /**
     * 常规模式
     */
    NORMAL = "normal",
    /**
     * 录播直播模式
     */
    PSEUDO = "pseudo"
}

export enum SwitchState {
    /**
     * 开
     */
    ON = "on",

    /**
     * 关
     */
    OFF = "off",
}

/**
 * 流状态
 */
export enum StreamState {
    /**
     * 未推流 (初始状态)
     */
    NONE = "none",

    /**
     * 推流中
     */
    PUSHING = "pushing",

    /**
     * 流切换中(由切换舞台时引起)
     */
    SWITCHING = "switching",

    /**
     * 重连中(由老师端推流中断重连引起)
     */
    RECONNECTING = "reconnecting",

    /**
     * 失去连接 (由老师离开直播间引起)
     */
    DISCONNECTED = "disconnected",
}

/**
 * 流播放器类型
 */
export enum StreamPlayerType {

    FLV_WASM = "FlvWasm",

    FLV_MSE = "FlvMse",

    HLS_MSE = "HlsMse",

    HLS_NATIVE = "HlsNative",
}

export enum StreamType {
    FLV = "flv",

    HLS = "hls",
}

/**
 * 回放状态
 */
export enum ReplayState {
    /**
     * 未开始
     */
    UNSTART = "unstart",

    /**
     * 生成中
     */
    GENERATING = "generating",

    /**
     * 已生成
     */
    FINISHED = "finished",

    /**
     * 已生成
     */
    NONE = "none",

    /**
     * 失败
     */
    ERROR = "error",
}

/**
 * 直播间连接状态
 */
export enum RoomConnectionState {
    /**
     * 未连接
     */
    UNCONNECTED = 'UNCONNECTED',

    /**
     * 已连接
     */
    CONNECTED = 'CONNECTED',

    /**
     * 连接中
     */
    CONNECTING = 'CONNECTING',

    /**
     * 已断开连接
     */
    DISCONNECTED = 'DISCONNECTED'
}

/**
 * 播放速度
 */
export enum VideoSpeed {


    RATE_1 = "0.5",

    RATE_2 = "0.75",

    RATE_3 = "1.0",

    RATE_4 = "1.25",

    RATE_5 = "1.5",

    RATE_6 = "2.0",
}

/**
 * 直播间计数器
 */
export interface RoomCounter {
    /**
     * 当前在线人数
     */
    online: number;

    /**
     * 最高在线人数
     */
    maxOnline: number;

    /**
     * 观看人次
     */
    enterNum: number;

    /**
     * 点赞数
     */
    likeNum: number;
}

export interface RoomVirtualUser {
    /**
     * 虚拟人数基数
     */
    virtualUserBase: number;
    /**
     * 实际人数倍数
     */
    virtualUserMultiple: number;
    /**
     * 虚拟用户名类型(truename, nickname, rand_letter)
     */
    virtualUserMode: string;
}

/**
 * 直播间登录用户
 */
export interface RoomUser {
    id: number;

    name: string;

    role: string;

    mute: boolean;

    groupNo: string;

    groupName: string;

    pmOnline: boolean;
}

export enum Role {

    /**
     * 主讲人(老师)
     */
    SPEAKER = 'speaker',

    /**
     * 助手(助教)
     */
    ASSISTANT = 'assistant',

    /**
     * 观众(学员)
     */
    VIEWER = 'viewer',

    /**
     * 技术支持
     */
    SUPPORTER = 'supporter',
}

export function isAdminRole(role: Role): boolean {
    return [Role.SPEAKER, Role.ASSISTANT, Role.SUPPORTER].includes(role);
}

export function isWatcherAdminRole(role: Role): boolean {
    return [Role.ASSISTANT, Role.SUPPORTER].includes(role);
}

export interface CheckIn {
    id: number;

    startAt: number;

    duration: number;

    checkInNum: number;
}

export interface EnteredQuestion {
    id: number;

    startAt: number;

    action: string;

    option: number;

    contentId: number;

    page: number;
}

export interface EnteredSpeaker {
    id: number;

    name: string;
}

export interface RoomGoods {
    id: number;

    roomId: number;

    /**
     * 商品编号
     */
    no: string;

    name: string;

    /**
     * 封面
     */
    picture: string;

    /**
     * 价格（分）
     */
    price: number;

    /**
     * 商品链接
     */
    url: string;

    /**
     * 已上架
     */
    onShelf: boolean;

    isDeleted: boolean;
}

export enum ScreenMode {
    LANDSCAPE = 'landscape',

    PORTRAIT = 'portrait',

    FAKE_LANDSCAPE = 'fake-landscape'
}

/**
 * 直播间基础字段
 */
export type RoomBasic = Pick<RoomStoreState, "id" | "name" | "status" | "mode" | "pseudoVideoUrl" | "startAt" | "endAt"
    | "actualStartAt" | "actualEndAt" | "hasOvertime" | "talkerLimit" | "recordEnable" | "recordStatus" | "replayEnable"
    | "remainTime" | "allMute" | "globalChatMute" | "forceChatOnly" | "showComboStats" | "leadQrcode" | "leadQrcodeUrl"
    | "sharePosterUrl" | "finishCallbackUrl" | "fileDomain">

export interface PingPayload {
    [propName: string]: string | number | boolean;
}

export interface PingPayloadCallbackFunc {
    (): PingPayload
}

